<template>
  <div class="wrapper">
    <!-- Page header -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h1 class="h4 mb-1 font-weight-bold">My payslips</h1>
            <p class="text-muted mb-0">Your monthly pay, deductions and year-to-date totals</p>
          </div>
          <CButton color="light" aria-label="Refresh" title="Refresh" @click="refreshTable">
            <CIcon name="cil-reload" />
          </CButton>
        </div>
      </CCardBody>
    </CCard>

    <CCard class="border-0">
      <!-- First load -->
      <CCardBody v-if="loading && !items.length" class="text-center py-5">
        <CSpinner color="primary" />
        <p class="text-muted mt-3 mb-0">Loading your payslips…</p>
      </CCardBody>

      <!-- Error -->
      <CCardBody v-else-if="loadError && !items.length" class="text-center py-5">
        <CIcon name="cil-warning" size="3xl" class="mb-3 text-danger" />
        <h2 class="h5">Couldn't load your payslips</h2>
        <p class="text-muted mb-3">Check your connection and try again.</p>
        <CButton color="primary" @click="refreshTable">
          <CIcon name="cil-reload" /> Try again
        </CButton>
      </CCardBody>

      <!-- Empty -->
      <CCardBody v-else-if="!items.length" class="text-center py-5">
        <CIcon name="cil-file" size="3xl" class="mb-3 text-muted" />
        <h2 class="h5">No payslips yet</h2>
        <p class="text-muted mb-0">Your payslips appear here once your employer runs payroll.</p>
      </CCardBody>

      <template v-else>
        <CProgress v-if="loading" :value="100" animated color="primary" size="xs" />
        <!-- Desktop table -->
        <CCardBody class="d-none d-lg-block">
          <CDataTable
            :items="rows"
            :fields="fields"
            items-per-page-select
            :items-per-page="25"
            hover
            sorter
            :sorter-value="{ column: 'date', asc: false }"
            pagination
            responsive
          >
            <template #date="{ item }">
              <td class="py-2">{{ formatDate(item.date) }}</td>
            </template>
            <template #payPeriod="{ item }">
              <td class="py-2">{{ item.payPeriod || "—" }}</td>
            </template>
            <template #businessName="{ item }">
              <td class="py-2">{{ item.businessName }}</td>
            </template>
            <template #totalAmount="{ item }">
              <td class="py-2 text-right font-weight-bold">{{ money(item.totalAmount) }}</td>
            </template>
            <template #statusDescription="{ item }">
              <td class="py-2">
                <CBadge :color="hrStatusColor('payslip', item.statusDescription)">
                  {{ hrStatusLabel('payslip', item.statusDescription) }}
                </CBadge>
              </td>
            </template>
            <template #actions="{ item }">
              <td class="py-2 text-right">
                <CButton color="light" size="sm" class="border" @click="showDetails(item)">
                  View
                </CButton>
              </td>
            </template>
          </CDataTable>
        </CCardBody>

        <!-- Phone / tablet cards -->
        <CCardBody class="d-lg-none p-2">
          <router-link
            v-for="item in sortedRows"
            :key="item.id"
            :to="`/employee/PaySlip/${item.id}`"
            class="payslip-card d-block border rounded p-3 mb-2 text-body"
          >
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <div class="font-weight-bold">{{ item.payPeriod || formatDate(item.date) }}</div>
                <small class="text-muted">{{ formatDate(item.date) }} · {{ item.businessName }}</small>
              </div>
              <CBadge :color="hrStatusColor('payslip', item.statusDescription)">
                {{ hrStatusLabel('payslip', item.statusDescription) }}
              </CBadge>
            </div>
            <div class="d-flex justify-content-between align-items-end mt-2">
              <span class="text-muted small">Net pay</span>
              <span class="font-weight-bold">{{ money(item.totalAmount) }}</span>
            </div>
          </router-link>
        </CCardBody>
      </template>
    </CCard>
  </div>
</template>

<script>
import PaySlipApi from "@/lib/paySlipApi";
import { formatDate, asDateString } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";
import { hrStatusMixin } from "@/utils/hrStatusBadges";

const fields = [
  { key: "date", label: "Date" },
  { key: "payPeriod", label: "Pay period" },
  { key: "businessName", label: "Employer" },
  { key: "totalAmount", label: "Net pay", _style: "text-align:right" },
  { key: "statusDescription", label: "Status" },
  { key: "actions", label: "", _style: "width:1%", sorter: false, filter: false },
];

export default {
  name: "PaySlipList",
  mixins: [hrStatusMixin],
  data() {
    return {
      loading: true,
      loadError: false,
      items: [],
      fields,
      api: new PaySlipApi(),
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", []);
    this.refreshTable();
  },
  computed: {
    rows() {
      return this.items.map((item) => ({
        ...item,
        businessName: (item.business && item.business.name) || "",
      }));
    },
    sortedRows() {
      return [...this.rows].sort((a, b) =>
        asDateString(b.date).localeCompare(asDateString(a.date))
      );
    },
  },
  methods: {
    formatDate,
    money(v) {
      return `RM ${formatMyr(v)}`;
    },
    showDetails(item) {
      this.$router.push({ path: `/employee/PaySlip/${item.id}` });
    },
    refreshTable() {
      this.loading = true;
      this.loadError = false;
      this.api
        .getListByCurrentProfile()
        .then((response) => {
          this.items = response.result || [];
        })
        .catch((err) => {
          console.error("Couldn't load payslips", err);
          this.loadError = true;
          if (this.items.length) {
            this.$toast.error("Couldn't refresh your payslips. Check your connection and try again.");
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.payslip-card:hover,
.payslip-card:focus {
  text-decoration: none;
}
</style>
